import { css } from "@emotion/react";
import { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router";

function FunnelIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill={active ? "#00685f" : "none"}
      stroke={active ? "#00685f" : "#64748b"}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

export function ActiveProjectsHealthCard({
  projects,
  fmtLatency,
  costFormatter,
}: {
  projects: any[];
  fmtLatency: (val: number) => string;
  costFormatter: (val: number) => string;
}) {
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [activePopover, setActivePopover] = useState<string | null>(null);

  // Sorting State
  const [sortColumn, setSortColumn] = useState<
    "project" | "traces" | "tokens" | "cost" | "latency" | "pii" | "status" | null
  >(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (
    col: "project" | "traces" | "tokens" | "cost" | "latency" | "pii" | "status"
  ) => {
    if (sortColumn === col) {
      if (sortDirection === "desc") {
        setSortDirection("asc");
      } else {
        setSortColumn(null);
        setSortDirection("desc");
      }
    } else {
      setSortColumn(col);
      setSortDirection(col === "project" ? "asc" : "desc");
    }
  };

  // Filter States
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [projectSearch, setProjectSearch] = useState("");

  const [tracesOp, setTracesOp] = useState<">=" | "<=" | "=">(">=");
  const [tracesVal, setTracesVal] = useState("");

  const [tokensOp, setTokensOp] = useState<">=" | "<=" | "=">(">=");
  const [tokensVal, setTokensVal] = useState("");

  const [costOp, setCostOp] = useState<">=" | "<=" | "=">(">=");
  const [costVal, setCostVal] = useState("");

  const [latencyOp, setLatencyOp] = useState<">=" | "<=" | "=">(">=");
  const [latencyVal, setLatencyVal] = useState("");

  const [selectedPii, setSelectedPii] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);

  // Draft States for Popovers
  const [draftProjects, setDraftProjects] = useState<string[]>([]);
  const [draftTracesOp, setDraftTracesOp] = useState<">=" | "<=" | "=">(">=");
  const [draftTracesVal, setDraftTracesVal] = useState("");
  const [draftTokensOp, setDraftTokensOp] = useState<">=" | "<=" | "=">(">=");
  const [draftTokensVal, setDraftTokensVal] = useState("");
  const [draftCostOp, setDraftCostOp] = useState<">=" | "<=" | "=">(">=");
  const [draftCostVal, setDraftCostVal] = useState("");
  const [draftLatencyOp, setDraftLatencyOp] = useState<">=" | "<=" | "=">(">=");
  const [draftLatencyVal, setDraftLatencyVal] = useState("");
  const [draftPii, setDraftPii] = useState<string[]>([]);
  const [draftStatus, setDraftStatus] = useState<string[]>([]);

  // Open popover handler syncs draft
  const togglePopover = (key: string) => {
    if (activePopover === key) {
      setActivePopover(null);
    } else {
      if (key === "project") setDraftProjects([...selectedProjects]);
      if (key === "traces") {
        setDraftTracesOp(tracesOp);
        setDraftTracesVal(tracesVal);
      }
      if (key === "tokens") {
        setDraftTokensOp(tokensOp);
        setDraftTokensVal(tokensVal);
      }
      if (key === "cost") {
        setDraftCostOp(costOp);
        setDraftCostVal(costVal);
      }
      if (key === "latency") {
        setDraftLatencyOp(latencyOp);
        setDraftLatencyVal(latencyVal);
      }
      if (key === "pii") setDraftPii([...selectedPii]);
      if (key === "status") setDraftStatus([...selectedStatus]);
      setActivePopover(key);
    }
  };

  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setActivePopover(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const evaluateNumericFilter = (
    val: number,
    op: ">=" | "<=" | "=",
    filterValStr: string
  ) => {
    if (!filterValStr || isNaN(Number(filterValStr))) return true;
    const target = Number(filterValStr);
    if (op === ">=") return val >= target;
    if (op === "<=") return val <= target;
    return val === target;
  };

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      // Project name filter
      if (
        selectedProjects.length > 0 &&
        !selectedProjects.includes(p.name)
      )
        return false;

      // Traces filter
      if (
        !evaluateNumericFilter(
          p.traceCount ?? 0,
          tracesOp,
          tracesVal
        )
      )
        return false;

      // Tokens filter
      if (
        !evaluateNumericFilter(
          p.costSummary?.total?.tokens ?? 0,
          tokensOp,
          tokensVal
        )
      )
        return false;

      // Cost filter
      if (
        !evaluateNumericFilter(
          p.costSummary?.total?.cost ?? 0,
          costOp,
          costVal
        )
      )
        return false;

      // Latency filter
      if (
        !evaluateNumericFilter(
          p.latencyMsP50 ?? 0,
          latencyOp,
          latencyVal
        )
      )
        return false;

      // PII Filter
      const piiCount = p.piiAnnotation?.count ?? 0;
      if (selectedPii.length > 0) {
        const hasPii = piiCount > 0;
        if (selectedPii.includes("Detected") && !hasPii) return false;
        if (selectedPii.includes("None") && hasPii) return false;
      }

      // Status Filter
      const latency = p.latencyMsP50 ?? 0;
      let statusStr = "Healthy";
      if (latency > 30000 || piiCount > 2) statusStr = "Critical";
      else if (latency > 10000 || piiCount > 0) statusStr = "Warning";
      if (
        selectedStatus.length > 0 &&
        !selectedStatus.includes(statusStr)
      )
        return false;

      return true;
    });
  }, [
    projects,
    selectedProjects,
    tracesOp,
    tracesVal,
    tokensOp,
    tokensVal,
    costOp,
    costVal,
    latencyOp,
    latencyVal,
    selectedPii,
    selectedStatus,
  ]);

  // Apply Column Sorting
  const sortedProjects = useMemo(() => {
    if (!sortColumn) return filteredProjects;

    return [...filteredProjects].sort((a, b) => {
      let aVal: any = 0;
      let bVal: any = 0;

      if (sortColumn === "project") {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else if (sortColumn === "traces") {
        aVal = a.traceCount ?? 0;
        bVal = b.traceCount ?? 0;
      } else if (sortColumn === "tokens") {
        aVal = a.costSummary?.total?.tokens ?? 0;
        bVal = b.costSummary?.total?.tokens ?? 0;
      } else if (sortColumn === "cost") {
        aVal = a.costSummary?.total?.cost ?? 0;
        bVal = b.costSummary?.total?.cost ?? 0;
      } else if (sortColumn === "latency") {
        aVal = a.latencyMsP50 ?? 0;
        bVal = b.latencyMsP50 ?? 0;
      } else if (sortColumn === "pii") {
        aVal = a.piiAnnotation?.count ?? 0;
        bVal = b.piiAnnotation?.count ?? 0;
      } else if (sortColumn === "status") {
        const getRank = (p: any) => {
          const lat = p.latencyMsP50 ?? 0;
          const pii = p.piiAnnotation?.count ?? 0;
          if (lat > 30000 || pii > 2) return 3;
          if (lat > 10000 || pii > 0) return 2;
          return 1;
        };
        aVal = getRank(a);
        bVal = getRank(b);
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredProjects, sortColumn, sortDirection]);

  const displayedProjects = showAllProjects
    ? sortedProjects
    : sortedProjects.slice(0, 10);

  const hasActiveFilters =
    selectedProjects.length > 0 ||
    tracesVal !== "" ||
    tokensVal !== "" ||
    costVal !== "" ||
    latencyVal !== "" ||
    selectedPii.length > 0 ||
    selectedStatus.length > 0;

  const clearAllFilters = () => {
    setSelectedProjects([]);
    setTracesVal("");
    setTokensVal("");
    setCostVal("");
    setLatencyVal("");
    setSelectedPii([]);
    setSelectedStatus([]);
    setSortColumn(null);
  };

  return (
    <div
      ref={popoverRef}
      css={css`
        background: var(--global-color-surface-lowest, #ffffff);
        border: 1px solid var(--global-border-color-default, #e1e3e4);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        margin-bottom: 24px;
        transition: all 0.3s ease;
        &:hover {
          border-color: var(--global-color-gray-300);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          flex-wrap: wrap;
          gap: 8px;
        `}
      >
        <div css={css`display: flex; align-items: center; gap: 12px;`}>
          <div
            css={css`
              font-size: 22px;
              font-weight: 900;
              color: #000000 !important;
              letter-spacing: -0.3px;
            `}
          >
            Active Projects Health
          </div>
          <span
            css={css`
              font-size: 13px;
              font-weight: 900;
              color: #00685f;
              background: #e0f2f1;
              padding: 2px 10px;
              border-radius: 12px;
            `}
          >
            Live Telemetry ({filteredProjects.length} Projects)
          </span>
        </div>

        <div css={css`display: flex; align-items: center; gap: 12px;`}>
          {/* Explicit Sort By Selector */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 13px;
              font-weight: 800;
              color: #000000 !important;
            `}
          >
            <span>Sort By:</span>
            <select
              value={sortColumn || "default"}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "default") {
                  setSortColumn(null);
                } else {
                  handleSort(val as any);
                }
              }}
              css={css`
                background: #ffffff;
                border: 1px solid #cbd5e1;
                border-radius: 8px;
                padding: 4px 8px;
                font-size: 13px;
                font-weight: 900;
                color: #0f172a;
                cursor: pointer;
                outline: none;
                &:focus {
                  border-color: #00685f;
                }
              `}
            >
              <option value="default">Default</option>
              <option value="project">Project Name (A-Z)</option>
              <option value="traces">Traces (High → Low)</option>
              <option value="tokens">Tokens (High → Low)</option>
              <option value="cost">Cost (High → Low)</option>
              <option value="latency">Latency (High → Low)</option>
              <option value="status">Status (Critical → Healthy)</option>
            </select>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              css={css`
                background: #f1f5f9;
                border: 1px solid #cbd5e1;
                border-radius: 8px;
                padding: 4px 10px;
                font-size: 12px;
                font-weight: 800;
                color: #ef4444;
                cursor: pointer;
                &:hover {
                  background: #fee2e2;
                }
              `}
            >
              Reset Filters ✖
            </button>
          )}
        </div>
      </div>

      <div css={css`overflow-x: auto; max-width: 100%;`}>
        <table
          css={css`
            width: 100%;
            border-collapse: collapse;
            text-align: left;
          `}
        >
          <thead>
            <tr
              css={css`
                background: #f8fafc;
                border-bottom: 2px solid #e1e3e4;
                font-size: 12px;
                font-weight: 900;
                color: #000000 !important;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              `}
            >
              {/* Project Header + Filter + Sort */}
              <th css={css`padding: 10px 12px; position: relative;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("project")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      user-select: none;
                      color: ${sortColumn === "project" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    PROJECT{" "}
                    {sortColumn === "project"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                  <button
                    onClick={() => togglePopover("project")}
                    title="Filter Projects"
                    css={css`
                      border: 1px solid
                        ${selectedProjects.length > 0 ? "#00685f" : "#cbd5e1"};
                      background: ${selectedProjects.length > 0
                        ? "#e0f2f1"
                        : "#ffffff"};
                      border-radius: 4px;
                      padding: 3px 5px;
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      &:hover {
                        border-color: #00685f;
                      }
                    `}
                  >
                    <FunnelIcon active={selectedProjects.length > 0} />
                  </button>
                </div>
                {activePopover === "project" && (
                  <div
                    css={css`
                      position: absolute;
                      top: 100%;
                      left: 0;
                      z-index: 50;
                      background: #ffffff;
                      border: 1px solid #cbd5e1;
                      border-radius: 10px;
                      padding: 12px;
                      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                      width: 200px;
                      font-weight: normal;
                      text-transform: none;
                    `}
                  >
                    <input
                      type="text"
                      placeholder="Search projects..."
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      css={css`
                        width: 100%;
                        box-sizing: border-box;
                        padding: 4px 8px;
                        font-size: 12px;
                        border: 1px solid #cbd5e1;
                        border-radius: 4px;
                        margin-bottom: 8px;
                      `}
                    />
                    <div
                      css={css`
                        max-height: 140px;
                        overflow-y: auto;
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                      `}
                    >
                      {projects
                        .map((p) => p.name)
                        .filter((name) =>
                          name
                            .toLowerCase()
                            .includes(projectSearch.toLowerCase())
                        )
                        .map((name) => (
                          <label
                            key={name}
                            css={css`
                              display: flex;
                              align-items: center;
                              gap: 6px;
                              font-size: 12px;
                              cursor: pointer;
                            `}
                          >
                            <input
                              type="checkbox"
                              checked={draftProjects.includes(name)}
                              onChange={(e) => {
                                if (e.target.checked)
                                  setDraftProjects([...draftProjects, name]);
                                else
                                  setDraftProjects(
                                    draftProjects.filter((n) => n !== name)
                                  );
                              }}
                            />
                            <span css={css`overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}>
                              {name}
                            </span>
                          </label>
                        ))}
                    </div>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                        margin-top: 10px;
                        border-top: 1px solid #f1f5f9;
                        padding-top: 8px;
                      `}
                    >
                      <button
                        onClick={() => {
                          setDraftProjects([]);
                          setSelectedProjects([]);
                          setActivePopover(null);
                        }}
                        css={css`
                          border: none;
                          background: transparent;
                          color: #64748b;
                          font-size: 12px;
                          font-weight: 700;
                          cursor: pointer;
                        `}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProjects([...draftProjects]);
                          setActivePopover(null);
                        }}
                        css={css`
                          border: none;
                          background: #00685f;
                          color: #ffffff;
                          font-size: 12px;
                          font-weight: 800;
                          padding: 4px 10px;
                          border-radius: 6px;
                          cursor: pointer;
                        `}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </th>

              {/* Traces Header */}
              <th css={css`padding: 10px 12px;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("traces")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      color: ${sortColumn === "traces" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    TRACES{" "}
                    {sortColumn === "traces"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                </div>
              </th>

              {/* Tokens Header */}
              <th css={css`padding: 10px 12px;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("tokens")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      color: ${sortColumn === "tokens" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    TOKENS{" "}
                    {sortColumn === "tokens"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                </div>
              </th>

              {/* Cost Header */}
              <th css={css`padding: 10px 12px;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("cost")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      color: ${sortColumn === "cost" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    COST{" "}
                    {sortColumn === "cost"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                </div>
              </th>

              {/* Latency Header */}
              <th css={css`padding: 10px 12px;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("latency")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      color: ${sortColumn === "latency" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    LATENCY (P50){" "}
                    {sortColumn === "latency"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                </div>
              </th>

              {/* PII Header */}
              <th css={css`padding: 10px 12px;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("pii")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      color: ${sortColumn === "pii" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    PII ANNOTATIONS{" "}
                    {sortColumn === "pii"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                </div>
              </th>

              {/* Status Header */}
              <th css={css`padding: 10px 12px; position: relative;`}>
                <div css={css`display: flex; align-items: center; gap: 6px;`}>
                  <button
                    type="button"
                    onClick={() => handleSort("status")}
                    css={css`
                      background: none;
                      border: none;
                      font: inherit;
                      padding: 0;
                      cursor: pointer;
                      color: ${sortColumn === "status" ? "#00685f" : "#0f172a"};
                      &:hover {
                        color: #00685f;
                      }
                    `}
                  >
                    STATUS{" "}
                    {sortColumn === "status"
                      ? sortDirection === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedProjects.length > 0 ? (
              displayedProjects.map((p) => {
                const latency = p.latencyMsP50 ?? 0;
                const piiCount = p.piiAnnotation?.count ?? 0;
                let status: "healthy" | "warning" | "critical" = "healthy";
                if (latency > 30000 || piiCount > 2) status = "critical";
                else if (latency > 10000 || piiCount > 0) status = "warning";

                const rowBg =
                  status === "critical"
                    ? "#fef2f2"
                    : status === "warning"
                      ? "#fffbeb"
                      : "#f8fafc";
                const dotBg =
                  status === "critical"
                    ? "#dc2626"
                    : status === "warning"
                      ? "#d97706"
                      : "#006b2d";
                const badgeBg =
                  status === "critical"
                    ? "#fee2e2"
                    : status === "warning"
                      ? "#fef9c3"
                      : "#dcfce7";
                const badgeText =
                  status === "critical"
                    ? "#7f1d1d"
                    : status === "warning"
                      ? "#78350f"
                      : "#064e3b";
                const statusLabel =
                  status === "critical"
                    ? "Critical"
                    : status === "warning"
                      ? "Warning"
                      : "Healthy";

                const projectTargetUrl = p.id
                  ? status !== "healthy"
                    ? `/projects/${p.id}/failure-analysis`
                    : `/projects/${p.id}/traces`
                  : "/projects";

                return (
                  <tr
                    key={p.id || p.name}
                    css={css`
                      background: ${rowBg};
                      border-bottom: 1px solid #e1e3e4;
                      transition: background 0.15s;
                      &:hover {
                        opacity: 0.95;
                      }
                    `}
                  >
                    <td
                      css={css`
                        padding: 12px;
                        font-size: 14px;
                        font-weight: 700;
                        color: #0f172a;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                      `}
                    >
                      <span
                        css={css`
                          width: 8px;
                          height: 8px;
                          border-radius: 50%;
                          background: ${dotBg};
                          flex-shrink: 0;
                        `}
                      />
                      <Link
                        to={projectTargetUrl}
                        css={css`
                          color: #00685f !important;
                          font-weight: 900;
                          font-size: 16px;
                          text-decoration: none;
                          &:hover {
                            text-decoration: underline;
                          }
                        `}
                      >
                        {p.name}
                      </Link>
                    </td>
                    <td css={css`padding: 12px; font-size: 14px; font-weight: 900; color: #00685f !important;`}>
                      {p.traceCount.toLocaleString()}
                    </td>
                    <td css={css`padding: 12px; font-size: 14px; font-weight: 800; color: #000000 !important;`}>
                      {(p.costSummary?.total?.tokens ?? 0).toLocaleString()}
                    </td>
                    <td css={css`padding: 12px; font-family: monospace; font-size: 13px; font-weight: 900; color: #000000 !important;`}>
                      {costFormatter(p.costSummary?.total?.cost ?? 0)}
                    </td>
                    <td css={css`padding: 12px; font-family: monospace; font-size: 13px; font-weight: 900; color: #000000 !important;`}>
                      {fmtLatency(latency)}
                    </td>
                    <td css={css`padding: 12px; font-size: 14px;`}>
                      {piiCount > 0 ? (
                        <span css={css`color: #78350f; font-weight: 900; display: flex; align-items: center; gap: 4px;`}>
                          ⚠️ {piiCount} detected
                        </span>
                      ) : (
                        <span css={css`color: #064e3b; font-weight: 800; display: flex; align-items: center; gap: 4px;`}>
                          ✓ None
                        </span>
                      )}
                    </td>
                    <td css={css`padding: 12px; font-size: 14px;`}>
                      <Link to={projectTargetUrl}>
                        <span
                          css={css`
                            background: ${badgeBg};
                            color: ${badgeText};
                            font-size: 12px;
                            font-weight: 900;
                            padding: 3px 12px;
                            border-radius: 20px;
                            display: inline-block;
                            cursor: pointer;
                            transition: transform 0.15s;
                            &:hover {
                              transform: scale(1.05);
                            }
                          `}
                        >
                          {statusLabel} {status !== "healthy" ? "🔍" : ""}
                        </span>
                      </Link>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={7}
                  css={css`
                    padding: 24px;
                    text-align: center;
                    color: #64748b;
                    font-size: 13px;
                    font-weight: 600;
                  `}
                >
                  No active projects match the selected column filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
          font-size: 13px;
          color: #000000 !important;
          font-weight: 800;
          padding-top: 8px;
          border-top: 1px solid #e1e3e4;
        `}
      >
        <span>
          Showing {displayedProjects.length} of {filteredProjects.length}{" "}
          projects{" "}
          {hasActiveFilters && `(filtered from ${projects.length})`}
        </span>
        <span
          onClick={() => setShowAllProjects((prev) => !prev)}
          css={css`
            color: #00685f !important;
            font-weight: 900;
            font-size: 14px;
            cursor: pointer;
            user-select: none;
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {showAllProjects
            ? "Show Top 10"
            : `View All ${filteredProjects.length} Projects →`}
        </span>
      </div>
    </div>
  );
}
